import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  Router,
  NavigationError,
  NavigationEnd,
  NavigationStart,
} from '@angular/router';
import {
  AuthService,
  FeatureFlagService,
  LoaderService,
} from '@icullen/api/shared';
import { ConnectivityService, GlobalConstants } from '@icullen/utils';
import { fromEvent, merge, Observable, of, Subscription } from 'rxjs';
import { filter, mapTo, skipWhile } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { MatSnackBar } from '@angular/material/snack-bar';
import posthog from 'posthog-js';
import { environment } from '@env/environment';

@Component({
  selector: 'icullen-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'site';
  private ignoreFirstOnlineStatus = true;
  private subs = new Subscription();

  constructor(
    private router: Router,
    public loaderService: LoaderService,
    private authService: AuthService,
    private connectivityService: ConnectivityService,
    private snackBar: MatSnackBar,
    private featureFlagService: FeatureFlagService
  ) {
    this.authService.setupAuthEvents();
  }

  ngOnInit(): void {
    //log 404 original urls
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (
          event.url !== event.urlAfterRedirects &&
          event.urlAfterRedirects.startsWith('/404')
        ) {
          console.log('404 error on the url : ' + event.url);
        }
      }
    });
    console.log('current user type: ' + this.authService.currentUser?.UserType);

    this.authService.authenticationEventObservable.subscribe((result) => {
      if (result) {
        this.addSentryFeedbackIntegration();
      }
    });
    this.addSentryFeedbackIntegration();

    this.subs = this.connectivityService
      .getOnlineStatus()
      .pipe(
        // Skip the first online status if it's the initial emission
        skipWhile((isOnline) => isOnline && this.ignoreFirstOnlineStatus)
      )
      .subscribe((isOnline) => {
        this.ignoreFirstOnlineStatus = false; // After the first check, always process status changes
        if (isOnline) {
          console.log(
            'Internet connection is restored for user : ' +
              this.authService.currentUser?.preferred_username
          );
          this.snackBar.open('Your internet connection is restored.', 'Close', {
            duration: 10000,
          });
          if (!this.authService.isAuthenticated()) {
            console.log(
              'User does not have valid token, hence refreshing token.'
            );
            this.authService.silentRefreshToken();
          }
        } else {
          console.log(
            'Internet connection is lost for user : ' +
              this.authService.currentUser?.preferred_username
          );
          this.snackBar.open('Your internet connection is lost.', 'Close', {
            duration: 10000,
          });
        }
      });
  }

  isExcludedRoute(): boolean {
    return this.router.url.indexOf(GlobalConstants.MENU_EXCLUDED_ROUTE) > -1;
  }

  private addSentryFeedbackIntegration() {
    console.log(
      'Adding Sentry feedback integration for user : ' +
        this.authService.currentUser?.preferred_username
    );
    
    if (
      this.featureFlagService.isFeatureEnabled(
        'sentryFeedback',
      )
    ) {
      Sentry.addIntegration(
        Sentry.feedbackIntegration({
          //colorScheme: 'light',
          themeLight: {
            submitBackground: '#0099a1',
            submitBorder: '#0099a1',
            submitBackgroundHover: '#0099a1',
            inputOutlineFocus: '#0099a1',
            fontFamily: 'Roboto, sans-serif',
          },
          showBranding: false,
          isNameRequired: true,
          isEmailRequired: true,
        })
      );
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
